import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useContextMenuContext } from "@components/ContextMenu/ContextMenu";

interface ContextMenuContentProps {
  children: (props: { close: () => void }) => React.ReactNode;
}

export const ContextMenuContent: React.FC<ContextMenuContentProps> = ({ children }) => {
  const ctx = useContextMenuContext();
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState<{ x: number; y: number } | null>(null);

  // Measure and adjust position to avoid viewport overflow
  useLayoutEffect(() => {
    if (!ctx.isOpen || !ctx.position || !menuRef.current) return;

    const MARGIN = 8;
    const rect = menuRef.current.getBoundingClientRect();
    let { x, y } = ctx.position;

    // Check bottom overflow
    if (y + rect.height > window.innerHeight - MARGIN) {
      y = Math.max(MARGIN, y - rect.height);
    }

    // Check right overflow
    if (x + rect.width > window.innerWidth - MARGIN) {
      x = Math.max(MARGIN, window.innerWidth - rect.width - MARGIN);
    }

    setAdjustedPos({ x, y });
  }, [ctx.isOpen, ctx.position]);

  // Handle click-outside and Escape
  useEffect(() => {
    if (!ctx.isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      ctx.close();
    };

    const handleContextMenu = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      ctx.close();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") ctx.close();
    };

    // Use capture phase to catch events before stopPropagation
    window.addEventListener("mousedown", handleMouseDown, true);
    window.addEventListener("contextmenu", handleContextMenu, true);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown, true);
      window.removeEventListener("contextmenu", handleContextMenu, true);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [ctx.isOpen, ctx.close]);

  if (!ctx.isOpen || !ctx.position) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: adjustedPos?.y ?? ctx.position.y,
        left: adjustedPos?.x ?? ctx.position.x,
        visibility: adjustedPos ? "visible" : "hidden",
        zIndex: 1000,
      }}
      className="w-56 rounded-lg border border-limestone-200 bg-white py-1 shadow-lg"
    >
      {children({ close: ctx.close })}
    </div>,
    document.body,
  );
};
